import React from 'react'
import { Button } from 'syft-acp-atoms/Button'
import { RateInputStepProps } from '../BulkShiftRateUpdateModal.types'
import { RateRangeInput } from './RateRangeInput'

export const RateInputStep: React.FC<RateInputStepProps> = ({
  rateRanges,
  rateUpdates,
  onRateChange,
  onBack,
  onNext,
  onCancel,
}) => {
  const isFixedRateCard = (range: any) => {
    const minRate =
      typeof range.min_payable_rate === 'string' ? parseFloat(range.min_payable_rate) : range.min_payable_rate
    const maxRate =
      typeof range.max_payable_rate === 'string' ? parseFloat(range.max_payable_rate) : range.max_payable_rate
    return minRate === maxRate
  }

  const validateRate = (rangeKey: string, rate: string) => {
    const range = rateRanges.find(r => `${r.start_date}_${r.end_date}` === rangeKey)
    if (!range) return undefined

    // Fixed rate cards are always valid (read-only)
    if (isFixedRateCard(range)) return undefined

    const numRate = parseFloat(rate)
    if (isNaN(numRate) || rate === '') return 'Rate is required'

    // Handle min/max as either string or number (API returns BigDecimal as string)
    const minRate =
      typeof range.min_payable_rate === 'string' ? parseFloat(range.min_payable_rate) : range.min_payable_rate
    const maxRate =
      typeof range.max_payable_rate === 'string' ? parseFloat(range.max_payable_rate) : range.max_payable_rate

    if (numRate < minRate) return `Rate must be at least ${minRate.toFixed(2)}`
    if (numRate > maxRate) return `Rate must not exceed ${maxRate.toFixed(2)}`
    return undefined
  }

  const allRatesEntered = rateRanges.every(range => {
    const rangeKey = `${range.start_date}_${range.end_date}`
    const rate = rateUpdates[rangeKey]
    return rate && !validateRate(rangeKey, rate)
  })

  return (
    <div>
      <h2 style={{ marginBottom: '1rem' }}>Update Shift Rates</h2>

      <div
        style={{
          padding: '1rem',
          backgroundColor: '#f0f8ff',
          border: '1px solid #4a90e2',
          borderRadius: '4px',
          marginBottom: '1rem',
        }}
      >
        <strong>ℹ️ Note:</strong> Different rate cards apply across these time periods. Enter a rate for each
        period within the allowed bounds.
      </div>

      <div style={{ marginBottom: '1rem' }}>
        {rateRanges.map(range => {
          const rangeKey = `${range.start_date}_${range.end_date}`
          const isFixed = isFixedRateCard(range)
          // Pre-populate fixed rate cards with their fixed rate
          const defaultRate = isFixed
            ? typeof range.min_payable_rate === 'string'
              ? range.min_payable_rate
              : String(range.min_payable_rate)
            : ''
          const rate = rateUpdates[rangeKey] || defaultRate
          const error = rate ? validateRate(rangeKey, rate) : undefined

          return (
            <RateRangeInput
              key={rangeKey}
              startDate={range.start_date}
              endDate={range.end_date}
              shiftCount={range.shift_count}
              rateCardId={range.rate_card_id}
              minRate={range.min_payable_rate}
              maxRate={range.max_payable_rate}
              currency={range.currency}
              clientRate={range.client_rate}
              value={rate}
              onChange={newRate => onRateChange(rangeKey, newRate)}
              error={error}
            />
          )
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button kind="regular" onClick={onBack}>
          Modify Date Range
        </Button>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button kind="neutral" onClick={onCancel}>
            Cancel
          </Button>
          <Button kind="primary" onClick={onNext} disabled={!allRatesEntered}>
            Review Changes
          </Button>
        </div>
      </div>
    </div>
  )
}
